/**
 * Client module for powerhive-backend REST API (Go / Rust service).
 * Provides access to timeseries telemetry, cell-level voltages, cabinet inventory,
 * mobility swap transactions, and cluster health metrics.
 */
import { getSupabase } from './supabase';

interface RFC7807Problem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  invalid_params?: Array<{ name: string; reason: string }>;
}

export interface BatteryLocationTelemetry {
  battery_id: string;
  latitude: number;
  longitude: number;
  altitude?: number;
  speed?: number;
  direction?: number;
  alarm_flag?: number;
  voltage?: number;
  current?: number;
  last_log_time: string;
}

export interface BatteryLiveState {
  battery_id: string;
  soc: number | null;
  soh?: number | null;
  voltage?: number | null;
  current?: number | null;
  temperatures?: number[];
  mos_charge?: boolean;
  mos_discharge?: boolean;
  cycles?: number;
  status?: string;
  last_updated?: string;
}

export interface BatteryCellVoltages {
  battery_id: string;
  cell_count?: number | null;
  min_voltage: number | null;
  max_voltage: number | null;
  delta_voltage: number | null;
  cell_voltages: number[];
  logged_at: string | null;
}

export interface CabinetSlotInventory {
  slot_number: number;
  door_id: string;
  battery_id: string | null;
  battery_serial?: string | null;
  battery_gps_id?: string | null;
  soc: number | null;
  status: 'ready' | 'charging' | 'empty' | 'faulty' | string;
  is_locked: boolean;
  last_seen: string;
}

export interface CabinetInventory {
  cabinet_id: string;
  total_slots: number;
  ready_batteries_count: number;
  charging_batteries_count: number;
  empty_slots_count: number;
  faulty_slots_count: number;
  occupancy_rate: number;
  slots: CabinetSlotInventory[];
  last_updated: string;
}

export function normalizeCabinetInventory(payload: unknown): CabinetInventory {
  const raw = rawObject(payload);
  const rawSlots = Array.isArray(raw.slots) ? raw.slots : [];
  return {
    cabinet_id: stringOrNull(raw.cabinet_id) ?? '',
    total_slots: finiteOrNull(raw.total_slots) ?? rawSlots.length,
    ready_batteries_count: finiteOrNull(raw.ready_batteries_count) ?? 0,
    charging_batteries_count: finiteOrNull(raw.charging_batteries_count) ?? 0,
    empty_slots_count: finiteOrNull(raw.empty_slots_count) ?? 0,
    faulty_slots_count: finiteOrNull(raw.faulty_slots_count) ?? 0,
    occupancy_rate: finiteOrNull(raw.occupancy_rate) ?? 0,
    slots: rawSlots.map((slot) => {
      const value = rawObject(slot);
      const serial = stringOrNull(value.battery_serial ?? value.battery_id);
      return {
        slot_number: finiteOrNull(value.slot_number) ?? 0,
        door_id: stringOrNull(value.door_id) ?? '',
        battery_id: serial,
        battery_serial: stringOrNull(value.battery_serial) ?? serial,
        battery_gps_id: stringOrNull(value.battery_gps_id),
        soc: finiteOrNull(value.soc),
        status: (stringOrNull(value.status) ?? 'empty') as CabinetSlotInventory['status'],
        is_locked: value.is_locked === true,
        last_seen: stringOrNull(value.last_seen) ?? '',
      };
    }),
    last_updated: stringOrNull(raw.last_updated) ?? '',
  };
}

export interface CellImbalanceAlert {
  battery_id: string;
  delta_voltage: number | null;
  min_voltage: number | null;
  max_voltage: number | null;
  threshold?: number | null;
  logged_at: string | null;
}

export interface SwapRecord {
  id: string;
  cabinet_id: string | null;
  customer_id?: string;
  customer_name?: string;
  deposited_battery_id?: string;
  dispatched_battery_id?: string;
  status: string | null;
  created_at: string | null;
  amount?: number;
  currency?: string;
}

export interface StationRanking {
  station_id: string;
  station_name: string;
  total_swaps: number;
  active_users: number;
  rank: number;
}

type RawRecord = Record<string, unknown>;

function rawObject(value: unknown): RawRecord {
  return value && typeof value === 'object' ? (value as RawRecord) : {};
}

function finiteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function boolMos(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (['on', 'enabled', 'enable', 'true', '1'].includes(normalized)) return true;
  if (['off', 'disabled', 'disable', 'false', '0'].includes(normalized)) return false;
  return undefined;
}

/** Raw Rust `/state` DTO -> stable UI telemetry model. */
export function normalizeBatteryLiveState(payload: unknown, fallbackId = ''): BatteryLiveState {
  const raw = rawObject(payload);
  const temperature = finiteOrNull(raw.temperature);
  const temperatures = Array.isArray(raw.temperatures)
    ? raw.temperatures.map(finiteOrNull).filter((v): v is number => v !== null)
    : temperature === null
      ? undefined
      : [temperature];
  return {
    battery_id: stringOrNull(raw.battery_id) ?? fallbackId,
    soc: finiteOrNull(raw.soc),
    soh: finiteOrNull(raw.soh),
    voltage: finiteOrNull(raw.voltage),
    current: finiteOrNull(raw.current),
    temperatures,
    mos_charge: boolMos(raw.charge_mos ?? raw.mos_charge),
    mos_discharge: boolMos(raw.discharge_mos ?? raw.mos_discharge),
    cycles: finiteOrNull(raw.cycles) ?? undefined,
    status: stringOrNull(raw.status) ?? undefined,
    last_updated: stringOrNull(raw.last_log_time ?? raw.last_updated) ?? undefined,
  };
}

/** Raw Rust `/cell-voltages` DTO -> ordered, nullable UI model. */
export function normalizeBatteryCellVoltages(
  payload: unknown,
  fallbackId = '',
): BatteryCellVoltages {
  const raw = rawObject(payload);
  const source = raw.cell_voltages;
  const cellVoltages = Array.isArray(source)
    ? source.map(finiteOrNull).filter((v): v is number => v !== null)
    : source && typeof source === 'object'
      ? Object.entries(source as RawRecord)
          .sort(
            ([a], [b]) => (Number(a.replace(/\D/g, '')) || 0) - (Number(b.replace(/\D/g, '')) || 0),
          )
          .map(([, value]) => finiteOrNull(value))
          .filter((v): v is number => v !== null)
      : [];
  const min = finiteOrNull(raw.min_voltage ?? raw.min_mv);
  const max = finiteOrNull(raw.max_voltage ?? raw.max_mv);
  return {
    battery_id: stringOrNull(raw.battery_id) ?? fallbackId,
    cell_count: finiteOrNull(raw.cell_count),
    min_voltage: min,
    max_voltage: max,
    delta_voltage:
      finiteOrNull(raw.voltage_delta ?? raw.delta_voltage ?? raw.delta_mv) ??
      (min !== null && max !== null ? max - min : null),
    cell_voltages: cellVoltages,
    logged_at: stringOrNull(raw.last_log_time ?? raw.logged_at),
  };
}

export function normalizeCellImbalanceAlert(payload: unknown): CellImbalanceAlert | null {
  const raw = rawObject(payload);
  const batteryId = stringOrNull(raw.battery_id);
  if (!batteryId) return null;
  return {
    battery_id: batteryId,
    delta_voltage: finiteOrNull(raw.voltage_delta ?? raw.delta_voltage),
    min_voltage: finiteOrNull(raw.min_voltage),
    max_voltage: finiteOrNull(raw.max_voltage),
    threshold: finiteOrNull(raw.threshold),
    logged_at: stringOrNull(raw.last_log_time ?? raw.logged_at),
  };
}

export function normalizeSwapRecord(payload: unknown): SwapRecord | null {
  const raw = rawObject(payload);
  const id = stringOrNull(raw.swap_id ?? raw.id);
  if (!id) return null;
  return {
    id,
    cabinet_id: stringOrNull(raw.cabinet_id),
    customer_id: stringOrNull(raw.customer_id) ?? undefined,
    customer_name: stringOrNull(raw.rider_name ?? raw.customer_name) ?? undefined,
    deposited_battery_id: stringOrNull(raw.deposited_battery_id) ?? undefined,
    dispatched_battery_id: stringOrNull(raw.dispatched_battery_id) ?? undefined,
    status: stringOrNull(raw.status ?? raw.swap_status),
    created_at: stringOrNull(raw.created_at),
    amount: finiteOrNull(raw.amount) ?? undefined,
    currency: stringOrNull(raw.currency) ?? undefined,
  };
}

export function normalizeStationRanking(payload: unknown): StationRanking | null {
  const raw = rawObject(payload);
  const id = stringOrNull(raw.cabinet_id ?? raw.station_id);
  if (!id) return null;
  return {
    station_id: id,
    station_name: stringOrNull(raw.cabinet_name ?? raw.station_name) ?? id,
    total_swaps: finiteOrNull(raw.total_swaps) ?? 0,
    active_users: finiteOrNull(raw.active_users) ?? 0,
    rank: finiteOrNull(raw.rank) ?? 0,
  };
}

export interface BackendClusterHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  latency_ms: number;
}

export interface BackendHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  clusters: {
    'powerhive-db'?: BackendClusterHealth;
    'powerhive-timeseries'?: BackendClusterHealth;
    'spark-ride-db'?: BackendClusterHealth;
  };
}

/** Configurable API base URL; defaults to production Rust API or Vite env var */
export function getPowerhiveBackendUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_POWERHIVE_BACKEND_URL) {
    return import.meta.env.VITE_POWERHIVE_BACKEND_URL.replace(/\/+$/, '');
  }
  return 'https://sparkapi.powerhive.com';
}

/**
 * Hardware commands go through the `battery-command` edge function, never
 * straight to batteryapi.powerhive.com. The function verifies the caller's JWT
 * and requires an admin role; calling the upstream directly from the browser
 * would send no credentials at all, leaving the "admin only" rule as nothing
 * but a `disabled` attribute. The upstream URL now lives server-side only.
 */
export function getBatteryCommandUrl(): string {
  const base = import.meta.env?.VITE_SUPABASE_URL;
  if (!base) throw new Error('Supabase URL is not configured');
  return `${String(base).replace(/\/+$/, '')}/functions/v1/battery-command`;
}

// Test seam: allow injecting custom fetch
let customFetch: typeof fetch | null = null;
export function setFetch(fn: typeof fetch | null): void {
  customFetch = fn;
}
function doFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return (customFetch ?? fetch)(input, init);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let errorDetail = `HTTP ${res.status}`;
    try {
      const problem = (await res.json()) as RFC7807Problem;
      if (problem.detail) errorDetail = problem.detail;
      else if (problem.title) errorDetail = `${problem.title} (${res.status})`;
    } catch {
      // JSON parse failed, use fallback status text
      if (res.statusText) errorDetail = `${res.statusText} (${res.status})`;
    }
    throw new Error(errorDetail);
  }
  return res.json() as Promise<T>;
}

/** Fetch live per-cell voltages and delta for a specific battery ID */
export async function getBatteryCellVoltages(
  batteryId: string,
  signal?: AbortSignal,
): Promise<BatteryCellVoltages> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/telemetry/batteries/${encodeURIComponent(batteryId)}/cell-voltages`;
  const res = await doFetch(url, { signal });
  const raw = await handleResponse<unknown>(res);
  return normalizeBatteryCellVoltages(raw, batteryId);
}

/** Fetch live operating telemetry & status for a battery */
export async function getBatteryLiveState(
  batteryId: string,
  signal?: AbortSignal,
): Promise<BatteryLiveState> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/telemetry/batteries/${encodeURIComponent(batteryId)}/state`;
  const res = await doFetch(url, { signal });
  const raw = await handleResponse<unknown>(res);
  return normalizeBatteryLiveState(raw, batteryId);
}

/** Fetch live GPS coordinates & vector for a battery */
export async function getBatteryLocation(
  batteryId: string,
  signal?: AbortSignal,
): Promise<BatteryLocationTelemetry> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/telemetry/batteries/${encodeURIComponent(batteryId)}/location`;
  const res = await doFetch(url, { signal });
  return handleResponse<BatteryLocationTelemetry>(res);
}

/** Fetch real-time cabinet slot inventory and occupancy */
export async function getCabinetInventory(
  cabinetId: string,
  signal?: AbortSignal,
): Promise<CabinetInventory> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/telemetry/cabinets/${encodeURIComponent(cabinetId)}/inventory`;
  const res = await doFetch(url, { signal });
  return normalizeCabinetInventory(await handleResponse<unknown>(res));
}

/** Fetch cell imbalance alerts across the active fleet */
export async function getCellImbalanceAlerts(
  threshold = 0.25,
  limit = 20,
  signal?: AbortSignal,
): Promise<CellImbalanceAlert[]> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/telemetry/alerts/cell-imbalance?threshold=${threshold}&limit=${limit}`;
  const res = await doFetch(url, { signal });
  const raw = await handleResponse<unknown>(res);
  return (Array.isArray(raw) ? raw : []).flatMap((item) => {
    const normalized = normalizeCellImbalanceAlert(item);
    return normalized ? [normalized] : [];
  });
}

/** Fetch recent battery swaps across the network or for a cabinet */
export async function getRecentSwaps(limit = 20, signal?: AbortSignal): Promise<SwapRecord[]> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/mobility/swaps?limit=${limit}`;
  const res = await doFetch(url, { signal });
  const raw = await handleResponse<unknown>(res);
  return (Array.isArray(raw) ? raw : []).flatMap((item) => {
    const normalized = normalizeSwapRecord(item);
    return normalized ? [normalized] : [];
  });
}

/** Fetch station swap performance rankings */
export async function getStationRankings(signal?: AbortSignal): Promise<StationRanking[]> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/mobility/stats/station-rankings`;
  const res = await doFetch(url, { signal });
  const raw = await handleResponse<unknown>(res);
  return (Array.isArray(raw) ? raw : []).flatMap((item) => {
    const normalized = normalizeStationRanking(item);
    return normalized ? [normalized] : [];
  });
}

/** Check health status across the 3 backend DB clusters */
export async function getBackendHealth(signal?: AbortSignal): Promise<BackendHealthResponse> {
  const url = `${getPowerhiveBackendUrl()}/api/v1/health`;
  const res = await doFetch(url, { signal });
  return handleResponse<BackendHealthResponse>(res);
}

/**
 * Dispatch an operational command to a battery device via the admin-gated
 * `battery-command` edge function. The caller's Supabase access token is the
 * credential the function checks — without a session the request is refused
 * before it ever reaches the hardware API.
 */
export async function sendBatteryCommand(
  deviceId: string,
  action: 'MOS_ON' | 'MOS_OFF' | 'REBOOT',
  signal?: AbortSignal,
): Promise<{ success: boolean; message?: string }> {
  const sb = getSupabase();
  const { data } = (await sb?.auth.getSession()) ?? { data: { session: null } };
  const token = data.session?.access_token;
  if (!token) {
    throw new Error('Sign in again — no active session to authorize this command.');
  }

  const res = await doFetch(getBatteryCommandUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ deviceId, action }),
    signal,
  });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error(`${action} refused — hardware commands require an admin role.`);
    }
    if (res.status === 429) {
      throw new Error(`${action} refused — too many commands, try again shortly.`);
    }
    throw new Error(`Command ${action} failed with HTTP ${res.status}`);
  }
  return res.json();
}
